// PRD 10 §2.2 #20 / §3.7 — Patient-app preview. Live render of the authored
// content (thumbnail + category badge + title + blocks), sanitized before
// `dangerouslySetInnerHTML`. Structure mirrors the prototype 1:1
// (health-encyclopedia.html `updatePreview`): status bar → white app header →
// content with placeholders (category → title → thumbnail → blocks). Reuses the
// global `mobile-preview` frame + `mg-rte-render` styles.

import { useTranslation } from 'react-i18next';

import { sanitizeHtml } from '@/shared/utils/sanitize-html';
import { VideoBlockPreview } from '@/components/data/VideoBlockPreview';

import type { ContentBlock } from '../types/health-encyclopedia.types';
import { ThumbnailWithFallback } from './EncyclopediaThumbnail';

interface EncyclopediaMobilePreviewProps {
  title: string;
  categoryName: string | null;
  thumbnailUrl: string | null;
  blocks: ContentBlock[];
}

export function EncyclopediaMobilePreview({
  title,
  categoryName,
  thumbnailUrl,
  blocks,
}: EncyclopediaMobilePreviewProps) {
  const { t } = useTranslation('health-encyclopedia');

  return (
    <div className="mobile-preview" style={{ display: 'flex', flexDirection: 'column' }}>
      {/* iOS-style status bar (prototype) */}
      <div className="mobile-preview__status-bar" style={{ padding: '4px 16px' }}>
        <span style={{ fontSize: 12, fontWeight: 600 }}>9:41</span>
      </div>
      {/* App header — white bg, centered title, bottom border (prototype). */}
      <div
        style={{
          height: 44,
          display: 'flex',
          alignItems: 'center',
          padding: '0 16px',
          borderBottom: '1px solid #f0f0f0',
          background: '#fff',
          flexShrink: 0,
        }}
      >
        <span style={{ flex: 1, textAlign: 'center', fontWeight: 600 }}>{t('preview.appHeader')}</span>
      </div>
      {/* Content — always renders placeholders (prototype shows the phone content
          on open, never a blank empty state). Figma order: category → title →
          thumbnail (inset) → body blocks. */}
      <div style={{ flex: 1, background: '#fff', overflowY: 'auto' }}>
        <div style={{ padding: 18 }}>
          <span
            style={{
              display: 'inline-block',
              padding: '4px 8px',
              background: '#eef6ff',
              color: '#1d4ed8',
              fontSize: 11,
              fontWeight: 600,
              borderRadius: 4,
              marginBottom: 12,
            }}
          >
            {categoryName ?? t('preview.categoryPlaceholder')}
          </span>
          <h1 style={{ fontSize: 20, fontWeight: 700, lineHeight: 1.4, margin: '0 0 12px 0', wordBreak: 'keep-all' }}>
            {title.trim() || t('preview.untitled')}
          </h1>
          {/* No thumbnail → "썸네일 미등록" placeholder text, matching the prototype —
              only a URL that fails to load falls back to the 기본 이미지 (SHCT-716),
              on the patient app's own surface colour rather than the admin token. */}
          {/* SHCT-649 — the box is 16:9, NOT a fixed height. PRD 10 v2.16 pins the thumbnail at
              16:9 (권장 1200×675) because 환자가 실제로 보는 것이 환자앱의 16:9 풀블리드 히어로다, and
              this preview stands in for that hero. A fixed 180px height only matched 16:9 at exactly
              320px wide; the phone frame is narrower, so `cover` cropped the sides off an image that
              was already the right ratio — QC hit this with a true 1200×675 file. The upload dropzone
              (EncyclopediaThumbnailUpload) has used `aspectRatio` since 649, which is why the same
              image looked correct there and clipped here. */}
          {thumbnailUrl ? (
            <div style={{ width: '100%', aspectRatio: '16 / 9', borderRadius: 8, overflow: 'hidden', margin: '0 0 12px' }}>
              <ThumbnailWithFallback
                url={thumbnailUrl}
                alt={title.trim() || t('preview.untitled')}
                style={{ width: '100%', height: '100%', objectFit: 'cover', background: '#f8f9fa' }}
              />
            </div>
          ) : (
            <div
              style={{
                width: '100%',
                // Same 16:9 box as the loaded state, so adding a thumbnail does not shift the layout.
                aspectRatio: '16 / 9',
                margin: '0 0 12px',
                borderRadius: 8,
                background: '#f8f9fa',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#adb5bd',
                fontSize: 13,
              }}
            >
              {t('preview.thumbnailEmpty')}
            </div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, color: '#4b5563', fontSize: 14, lineHeight: 1.6 }}>
            {blocks.length === 0 ? (
              t('preview.noBlocks')
            ) : (
              blocks.map((block) => <PreviewBlock key={block.id} block={block} />)
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function PreviewBlock({ block }: { block: ContentBlock }) {
  const { t } = useTranslation('health-encyclopedia');

  if (block.type === 'text') {
    const body = (block.text ?? '').trim();
    const html = body ? sanitizeHtml(body) : '';
    return (
      <div style={{ marginBottom: 10 }}>
        {html ? (
          // Sanitized above — safe to render.
          <div className="mg-rte-render" dangerouslySetInnerHTML={{ __html: html }} />
        ) : (
          <span style={{ color: 'var(--muted-foreground)' }}>{t('preview.textBlock')}</span>
        )}
      </div>
    );
  }

  // PRD v2.5 §3.7 — image/video blocks render an optional title above the media
  // and the description below it, the latter only when "텍스트 설명 노출" is on
  // (prototype `updatePreview`: bTitle / bDesc).
  const blockTitle = (block.title ?? '').trim();
  const description = (block.text ?? '').trim();
  const titleNode = blockTitle ? <div style={{ fontWeight: 600, marginBottom: 4 }}>{blockTitle}</div> : null;
  const descNode =
    block.showText && description ? (
      <div style={{ fontSize: 13, color: 'var(--muted-foreground)', marginTop: 4 }}>{description}</div>
    ) : null;

  if (block.type === 'image') {
    return (
      <div style={{ marginBottom: 10, textAlign: 'center' }}>
        {titleNode}
        {block.dataUrl ? (
          <img src={block.dataUrl} alt="" style={{ maxWidth: '100%', borderRadius: 8 }} />
        ) : (
          <div
            style={{
              padding: 24,
              border: '1px dashed #d1d5db',
              borderRadius: 8,
              background: '#f9fafb',
              textAlign: 'center',
              color: '#9ca3af',
            }}
          >
            {t('preview.imageBlock')}
          </div>
        )}
        {descNode}
      </div>
    );
  }

  // Video — embed YouTube/Vimeo so the preview matches the patient app. SHCT-370: an
  // unrecognized URL falls back to the "영상 보기" card, not the raw address
  // (PRD 10 v2.15 §3.7 [ADM.044]); only a block with NO url keeps the old placeholder.
  const url = (block.url ?? '').trim();
  return (
    <div style={{ marginBottom: 10 }}>
      {titleNode}
      {url ? (
        <VideoBlockPreview
          url={url}
          title={blockTitle || 'video'}
          watchLabel={t('preview.watchVideo')}
        />
      ) : (
        <div
          style={{
            padding: '10px 12px',
            background: '#eef2ff',
            borderRadius: 8,
            color: '#4338ca',
          }}
        >
          {t('preview.noVideoUrl')}
        </div>
      )}
      {descNode}
    </div>
  );
}
